import { ReactElement, useCallback, useMemo } from 'react'
import classNames from 'classnames'
import Image from '../ChakraNextImage/index'
import { GalleryImage, ImageGalleryProps } from './ImageGallery.types'
import useGalleryArrowNavigation from './useGalleryArrowNavigation'

import altoStyles from './ImageGallery.module.scss'

function ImageGallery({
  images,
  onImageClick,
  defaultSize,
  fallbackSrc,
}: ImageGalleryProps): ReactElement<React.PropsWithChildren> {
  const gallerySize = useMemo<number>(() => {
    if (images.length === 0) {
      return defaultSize
    }
    if (images.length < 3) {
      return 1
    }
    if (images.length < 7) {
      return 3
    }
    return 7
  }, [images, defaultSize])

  const galleryImages = useMemo<(GalleryImage | undefined)[]>(() => {
    return images.length === 0
      ? new Array<undefined>(gallerySize).fill(undefined)
      : images.slice(0, gallerySize)
  }, [images, gallerySize])

  const { onKeyDown, resetTabIndex, registerImageButton } = useGalleryArrowNavigation()

  const handleImageClick = (_index: number) => {
    let index = 0
    if (_index < gallerySize - 1) {
      index = _index
    }
    onImageClick(index)
    resetTabIndex()
  }

  const getImageLabel = useCallback(
    (i: number, title?: string | null) => {
      const label = title ? `Photo of ${title}` : ''
      if (i === galleryImages.length - 1) {
        return `${label}, click to see more photos`
      }
      return label
    },
    [galleryImages.length]
  )

  return (
    <div
      className={classNames(
        altoStyles.container,
        gallerySize < 3 ? altoStyles.gallerySize12 : altoStyles.gallerySize8
      )}
      aria-label="image gallery"
      tabIndex={0}
      role="button"
      onKeyDown={onKeyDown}
    >
      {galleryImages.map((img, index) => (
        <button
          className={altoStyles.button}
          type="button"
          onClick={handleImageClick.bind(null, index)}
          key={`img-${img ? img.imageUrl : index}`}
          disabled={img === undefined}
          data-remaining-images={`${images.length - index - 1}+`}
          {...registerImageButton(index)}
        >
          {img && (
            <Image
              layout="fill"
              objectFit="cover"
              src={img.imageUrl ?? fallbackSrc}
              alt={getImageLabel(index, img.title)}
              data-testid="gallery-image"
            />
          )}
        </button>
      ))}
    </div>
  )
}

export default ImageGallery
